import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import EventFinderAPI from '../services/api';

/**
 * Redirects legacy `/browse` link shapes onto their canonical routes:
 *
 *   ?entity=<name>                  -> /<type>/<slug>/events
 *   ?city=<name>&geoCity=1          -> /location/<slug>/events
 *
 * Old links and existing bookmarks must keep working, so both are resolved
 * server-side — that also covers entities renamed since the bookmark was made.
 * Anything that does not resolve falls through to the wrapped page unchanged.
 */
export const LegacyEntityRedirect: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const entityName = searchParams.get('entity');
  const city = searchParams.get('city');
  const isGeoCity = searchParams.get('geoCity') === '1';
  const shouldRedirect = Boolean(entityName) || Boolean(city && isGeoCity);

  const [isResolving, setIsResolving] = useState(shouldRedirect);

  useEffect(() => {
    if (!shouldRedirect) {
      setIsResolving(false);
      return;
    }

    const controller = new AbortController();
    let cancelled = false;
    setIsResolving(true);

    const carryOverParams = (...consumed: string[]) => {
      const carried = new URLSearchParams(searchParams);
      consumed.forEach((key) => carried.delete(key));
      const search = carried.toString();
      return search ? `?${search}` : '';
    };

    (async () => {
      try {
        if (entityName) {
          const resolution = await EventFinderAPI.resolveEntity({ entityName }, controller.signal);
          if (cancelled) return;
          const target = resolution?.entity?.canonicalUrl;
          if (target) {
            navigate({ pathname: target, search: carryOverParams('entity') }, { replace: true });
            return;
          }
        } else if (city) {
          const resolution = await EventFinderAPI.resolveLocation(
            {
              city,
              state: searchParams.get('state') || undefined,
              country: searchParams.get('country') || undefined,
            },
            controller.signal,
          );
          if (cancelled) return;
          const target = resolution?.location?.canonicalUrl;
          if (target) {
            navigate(
              { pathname: target, search: carryOverParams('city', 'state', 'country', 'geoCity', 'lat', 'lng') },
              { replace: true },
            );
            return;
          }
        }
      } catch {
        // A failed lookup must not strand the user — fall back to /browse.
      }
      if (!cancelled) setIsResolving(false);
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [shouldRedirect, entityName, city, navigate, searchParams]);

  if (isResolving) return null;
  return <>{children}</>;
};

export default LegacyEntityRedirect;
